'use client';

import React from 'react';
import Image, { type ImageProps } from 'next/image';
import { cn } from '@/components/ui/_lib/cn';
import type { UiSkeletonProps } from '@/components/ui/_lib/skeletonProps';
import { useDeferredSkeleton } from '@/components/ui/_lib/useDeferredSkeleton';
import { SKELETON_BY_COMPONENT } from '@/components/ui/_lib/skeletonRegistry';

const sk = SKELETON_BY_COMPONENT.Img;

export type ImgProps = ImageProps &
  UiSkeletonProps & {
    className?: string;
    /** Alternative text for screen readers and SEO. */
    alt: string;
    title?: string;
    role?: 'img' | 'presentation';
    loading?: 'lazy' | 'eager';
    decoding?: 'async' | 'auto' | 'sync';
  };

const Img: React.FC<ImgProps> = ({
  className,
  alt,
  title,
  role = 'img',
  loading,
  decoding = 'async',
  priority,
  skeleton = false,
  skeletonMaxWaitMs = 2000,
  ...props
}) => {
  const showSkeleton = useDeferredSkeleton(skeleton, skeletonMaxWaitMs);

  if (showSkeleton) {
    return (
      <span
        className={cn('skeleton', `skeleton-${sk.shape}`, 'img', 'block', className)}
        aria-hidden
      />
    );
  }

  return (
    <Image
      className={cn('img', 'object-cover', 'select-none', className)}
      alt={alt}
      title={title || alt}
      role={role}
      decoding={decoding}
      loading={priority ? undefined : loading || 'lazy'}
      priority={priority}
      {...props}
    />
  );
};

export default Img;
